<template>
  <div class="header dark:bg-darkblue hidden lg:flex border-b border-solid border-gray-10/50 dark:border-darkblue-600">
    <div class="brand w-full lg:w-72 lg:min-w-aside flex items-center justify-center select-none">
      <router-link :to="{ name: 'login' }" style="width: 200px">
        <base-logo 
          :filename="logo"
          :apiImage="true"
          :alt="$t('app.base_logo')" 
          class="max-h-13" 
          data-v-step="0" />
      </router-link>
    </div>
    <div class="w-full flex items-center justify-between responsive-spacing-y px-10  select-none">
      <div class="flex items-center ml-auto">
        <div class="global-tools h-11 flex items-center space-x-2 mr-4">
          <div v-if="!(isRoleUserConsulta || isRoleProvider) && hasModuleInterfazEmision" class="relative z-2010">
            <div ref="emision"
              v-outside:click="{ exclude: ['emision'], handler: closeEmision }"
              class="relative dropdown-area">
              <base-button v-if="isIssuingCreation" @click="issuingButton.handler" :mode="issuingModeActive" class="w-full"
                :text="issuingButton.text" :icon="issuingButton.icon" :disabled="issuingButton.disabled" />
              <template v-else>
                <base-avatar :isOpen="emision" @toggleAvatar="toggleEmision" :customText="$t('invoices.invoices_issuing.issue')" :showAvatar="false"
                  :custom="['hover:bg-primary-50', { 'bg-primary-50': emision }, 'dark:bg-darkblue-800', 'dark:hover:bg-darkblue-600', 'dark:text-darkblue-200']"/>
                <base-drop-down :isOpen="emision" autoClose position="center" @toggle="toggleEmision">
                  <ul class="p-1 divide-y divide-gray-50 dark:divide-gray-800 dark:border-darkblue-600">
                    <base-link-item-nav v-for="option in emisionOptions" :option="option" :key="option.name" />
                  </ul>
                </base-drop-down>
              </template>
            </div>
            <span v-show="actualFilterIssuing" class='badge badge-warning absolute top-2 left-2 z-20 badge-count-visited'
              v-popover:popoverIssuing>
              <base-icon name="filter" size="xs" />
            </span>
            <el-popover ref="popoverIssuing" placement="bottom" width="320" trigger="hover"
              popper-class="dark:bg-darkblue dark:border-darkblue-600 dark:text-darkblue-200">
              <div>
                <div v-if="actualFilterIssuing">
                  <div class="flex items-center justify-between">
                    <span class="text-lg">{{$t('app.client_filter')}}</span>
                    <base-button-icon @click="resetIssuingFilter()" name="cross" iconSize="xs" size="xs"
                      mode="primary-phantom"
                      class="ml-2 bg-primary-100 hover:bg-primary-200 dark:bg-darkblue-800 dark:text-darkblue-200 rounded-full" />
                  </div>
                  <div class="flex items-center justify-between">
                    <span>{{ actualFilterIssuing.idCliente }} - {{ actualFilterIssuing.Nombre }}</span>
                  </div>
                </div>
                <div v-else>
                  <span>{{$t('app.empty_client_filter')}}</span>
                </div>
              </div>
            </el-popover>
          </div>
          <div>
            <div v-if="!(isRoleUserConsulta || isRoleProvider) && hasModuleInvoiceOrVerifactu" class="relative">
              <base-button @click="toggleUploadInvoice" mode="core" class="w-full"
                :text="$tc('invoices.dialogs.upload_invoice', 2)" icon="export" :loading="uploadingInvoices" :disabled="uploadingInvoices" />
              <span v-show="actualFilterUploads" class='badge badge-warning absolute top-2 left-2 z-20 badge-count-visited'
                v-popover:popoverUpload>
                <base-icon name="filter" size="xs" />
              </span>
              <el-popover  ref="popoverUpload" placement="bottom" width="320" trigger="hover"
                popper-class="dark:bg-darkblue dark:border-darkblue-600 dark:text-darkblue-200">
                <div>
                  <div v-if="actualFilterUploads">
                    <div class="flex items-center justify-between">
                      <span class="text-lg">{{$t('app.client_filter')}}</span>
                      <base-button-icon @click="resetUploadsFilter()" name="cross" iconSize="xs" size="xs"
                        mode="primary-phantom"
                        class="ml-2 bg-primary-100 hover:bg-primary-200 dark:bg-darkblue-800 dark:text-darkblue-200 rounded-full" />
                    </div>
                    <div class="flex items-center justify-between">
                      <span>{{ actualFilterUploads.idCliente }} - {{ actualFilterUploads.Nombre }}</span>
                    </div>
                  </div>
                  <div v-else>
                    <span>{{$t('app.empty_client_filter')}}</span>
                  </div>
                </div>
              </el-popover>
            </div>
          </div>
          <div ref="dashboard" class="relative dropdown-area">
            <div class="relative" @click="gotoDashboard()">
              <base-button-icon name="dashboard" mode="core" />
            </div>
          </div>
          <div ref="notifications" v-outside:click="{ exclude: ['notifications'], handler: closeNotifications }"
            class="relative dropdown-area">
            <div class="relative" @click="toggleNotifications">
              <base-button-icon name="bell" mode="core" />
              <span v-if="announcements !== null && announcements.length > 0" class='badge badge-warning absolute top-2 right-0 z-20' :class="alreadyVisited ? 'badge-count-visited' : 'badge-count'"> {{ $n(total, 'integer') }} </span>
              <span v-show="actualFilterAlerts" class='badge badge-warning absolute top-2 left-2 z-20 badge-count-visited'
                v-popover:popover>
                <base-icon name="filter" size="xs" />
              </span>
              <el-popover ref="popover" placement="bottom" width="320" trigger="hover"
                popper-class="dark:bg-darkblue dark:border-darkblue-600 dark:text-darkblue-200">
                <div>
                  <div v-if="actualFilterAlerts">
                    <div class="flex items-center justify-between">
                      <span class="text-lg">{{$t('app.client_filter')}}</span>
                      <base-button-icon @click="resetAlertsFilter()" name="cross" iconSize="xs" size="xs"
                        mode="primary-phantom"
                        class="ml-2 bg-primary-100 hover:bg-primary-200 dark:bg-darkblue-800 dark:text-darkblue-200 rounded-full" />
                    </div>
                    <div class="flex items-center justify-between">
                      <span>{{ actualFilterAlerts.idCliente }} - {{ actualFilterAlerts.Nombre }}</span>
                    </div>
                  </div>
                  <div v-else>
                    <span>{{$t('app.empty_client_filter')}}</span>
                  </div>
                </div>
              </el-popover>
            </div>
            <base-drop-down :isOpen="nots" position="bottom-right" customClass="w-50 z-2010imp" autoClose
              @toggle="toggleNotifications">
              <div class="p-4">
                <div v-if="announcements != null && announcements.length > 0">
                  <div class="lg:mb-4 flex -mx-2 flex-col lg:flex-row" v-for="(announcement, index) in announcements"
                    :key="index">
                    <div class="px-2 w-full  mb-2 lg:mb-0">
                      <router-link v-if="announcement.Enlace !== null" :to="{ name: announcement.Enlace }" href="#"
                        class="child w-full flex items-center  pr-4 h-8
                            hover:bg-primary-100 dark:hover:bg-darkblue-900 active:bg-primary-200 group animate-hover rounded">
                        <span
                          class="cover-subnav w-full overflow-hidden pl-2 flex h-full border-l-2 border-solid items-center dark:border-darkblue-600">
                          <span class="link-name mr-4 truncate group-hover:text-primary dark:text-secondary-200">{{
                            announcement.Mensaje }}</span>
                        </span>
                      </router-link>
                      <div v-else-if="announcement.Accion !== null" @click="onclickAnnouncement(announcement.Accion)"
                        class="child w-full flex items-center pr-4 h-8 cursor-pointer hover:bg-primary-100 dark:hover:bg-darkblue-900 active:bg-primary-200 group animate-hover rounded">
                        <span class="cover-subnav w-full overflow-hidden pl-2 flex h-full border-l-2 border-solid items-center dark:border-darkblue-600">
                          <span class="link-name mr-4 truncate group-hover:text-primary dark:text-secondary-200">{{ announcement.Mensaje }}</span>
                        </span>
                      </div>
                      <span v-else class="cover-subnav w-full overflow-hidden pl-2 flex h-full border-l-2 border-solid items-center dark:text-secondary-200 dark:border-darkblue-600">{{ announcement.Mensaje }}</span>
                    </div>
                    <div class="px-2 mt-1 mb-2 lg:mb-0">
                      <span v-if="announcement.NumeroAvisos !== 0"
                        class='badge badge-warning right-0 z-20 cart-count active:bg-primary-200'>
                        {{ $n(announcement.NumeroAvisos, 'integer') }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="flex items-center justify-center w-32 dark:text-secondary-200" v-else>
                  <p>{{ $t('avisos.announcements_empty') }}</p>
                </div>
              </div>
            </base-drop-down>
          </div>
        </div>
        <div ref="avatar"
            v-outside:click="{ exclude: ['avatar'], handler: closeAvatar }"
            class="relative dropdown-area z-2010">
          <base-avatar :isOpen="avatar" @toggleAvatar="toggleAvatar"
            :custom="['hover:bg-primary-50', { 'bg-primary-50': avatar }, 'dark:bg-darkblue-800', 'dark:hover:bg-darkblue-600', 'dark:text-darkblue-200']" />
          <base-drop-down :isOpen="avatar" autoClose position="bottom-right" @toggle="toggleAvatar">
            <div class="p-1">
              <p @click="changeDarkMode()"
                class="capitalize cursor-pointer focus:outline-none group flex rounded-md items-center w-full px-2 py-2 text-sm bg-white dark:bg-darkblue dark:text-darkblue-200 lg:dark:bg-darkblue-900 hover:bg-primary-50 dark:hover:bg-darkblue hover:text-primary active:bg-primary-100 dark:active:bg-darkblue-600 dark:focus:bg-darkblue-600 dark:focus:text-primary">
                <base-icon name="darktheme" class="mr-2" />
                {{ mode }}
              </p>
            </div>

            <ul class="p-1 divide-y divide-gray-50 dark:divide-gray-800 dark:border-darkblue-600">
              <base-link-item-nav v-for="option in userOptions" :option="option" :key="option.name" />
            </ul>
          </base-drop-down>
        </div>
      </div>
    </div>
    <help-dialog :dialogOpen="helpDialogOpen" @toggleDialog="toggleDialog" />
    <upload-invoice-dialog :dialogOpen="uploadInvoiceDialog" @toggleDialog="toggleUploadInvoice"
      @applyDialog="applyUploadInvoice" />
    <UploadInvoicesProgressDialog 
      :dialogOpen="uploadInvoiceProgressData.isOpen" :params="uploadInvoiceProgressData" 
      :recibidas="uploadInvoiceProgressData.recibidas" @toggleDialog="toggleUploadProgressInvoice"
      @applyDialog="applyUploadProgressInvoice" @resetDialog="resetUploadProgressInvoice" />
  </div>
</template>

<script>
import { bus } from '@/main'
import { i18n } from '@/i18n'
import { v4 as uuidv4 } from 'uuid'
import HelpDialog from '@/components/HelpDialog.vue'
// import LicenseDialog from '@/components/LicenseDialog.vue'
import UploadInvoiceDialog from '@/components/UploadInvoiceDialog.vue'
import UploadInvoicesProgressDialog from '@/components/UploadInvoicesProgressDialog.vue'
import { setTransitionAllowed } from '@/services/functions/modeAllowedTransitionHelper'

export default {
  name: 'header-core',
  components: {
    HelpDialog,
    UploadInvoiceDialog,
    UploadInvoicesProgressDialog,
  },
  mounted () {
    this.$root.$on('openNotesPatch', () => {
      this.showHelp()
    })
  },
  data: function () {
    return {
      uploadInvoiceDialog: false,
      helpDialogOpen: false,
      nots: false,
      alreadyVisited: false,
      total: 0,
      avatar: false,
      emision: false,
      uuidList: [],
      productSelect: [
      ],
      configOptions: [
      ],
      uploadInvoiceProgressData: {
        isOpen: false,
        idCliente: 0,
        idCIFEmpresa: 0,
        pgeType: null,
        autoSend: false,
        facturas: [],
        recibidas: false,
      },
      uploadingInvoices: false,
    }
  },
  computed: {
    isRoleProvider: function () {
      return this.role === 9
    },
    actualFilterAlerts () {
      return this.$store.getters['app/idClienteFilterAlerts']
    },
    actualFilterUploads () {
      return this.$store.getters['app/idClienteFilterUpload']
    },
    actualFilterIssuing () {
      return this.$store.getters['app/idClienteFilterIssuing']
    },
    isIssuingCreation: function () {
      return !!this.$store.getters['app/isInMode'](1)
    },
    issuingModeActive: function () {
      return this.isIssuingCreation ? 'primary' : 'core'
    },
    issuingType: function () {
      return this.$store.getters['invoicesIssuing/issuingType']
    },
    issuingButton: function () {
      const button = { text: '', icon: 'docblack', handler: event => {}, disabled: !this.isIssuingCreation }
      switch (this.issuingType) {
        case 4:
          button.text = i18n.t('invoices.invoices_issuing.simplified.header')
          button.handler = this.gotoSimplified
          break
        case 3:
          button.text = i18n.t('invoices.invoices_issuing.quotation.header')
          button.handler = this.gotoQuotation
          break
        case 2:
          button.text = i18n.t('invoices.invoices_issuing.proforma.header')
          button.handler = this.gotoProforma
          break
        case 1:
        default:
          button.text = i18n.t('invoices.invoices_issuing.header')
          button.handler = this.gotoIssuing
          break
      }
      return button
    },
    role: function () {
      return this.$store.getters['auth/role']
    },
    isRoleUserConsulta: function () {
      return this.role === 4 || this.role === 7
    },
    darkMode: function () {
      return !!this.$store.getters['app/darkMode']
    },
    lcbuttonMode: function () {
      return this.darkMode ? 'dark' : 'light'
    },
    logo: function () {
      const pathBase = process.env.VUE_APP_API_URL + 'api/personalizacion/getrecurso?hash='
      const user = this.$store.getters['auth/user']

      // Personalizacion de logo
      if (user) {
        if (this.darkMode) {
          if (user.HashLogoDark) { 
            return pathBase + user.HashLogoDark 
          }
        } else {
          if (user.HashLogo) {
            return pathBase + user.HashLogo
          }
        }
      }

      if (this.darkMode) {
        return '/logos/logo-edasinvoize-dark.webp'
      }
      return '/logos/logo-edasinvoize.webp'
    },
    tiposAviso: function () {
      return this.$store.getters['config/TIPOS_AVISO']
    },
    announcements: function () {
      const announcements = []
      const value = this.$store.getters['app/getAnnouncementsList']
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.total = 0
      if (value != null) {
        value.forEach(element => {
          const aviso = this.tiposAviso.find(x => element.TipoAviso === x.TipoAviso)
          element.Mensaje = aviso?.Mensaje ?? element.Mensaje
          announcements.push(element)
          this.total += element.NumeroAvisos
        })
      }
      return announcements
    },
    mode: function () {
      if (this.darkMode === true) {
        return i18n.t('app.mode_light')
      } else {
        return i18n.t('app.mode_dark')
      }
    },
    emisionOptions: function () {
      const issuing = this.gotoIssuing
      const proforma = this.gotoProforma
      const presupuesto = this.gotoQuotation
      const simplificada = this.gotoSimplified
      return [
        {
          icon: 'send',
          name: i18n.t('invoices.invoices_issuing.header'),
          disabled: this.issuingType === 1 && this.$route.name === 'invoicesissuing',
          click: function () {
            issuing()
          }
        },
        {
          icon: 'send',
          name: i18n.t('invoices.invoices_issuing.proforma.header'),
          disabled: this.issuingType === 2 && this.$route.name === 'invoicesissuing',
          click: function () {
            proforma()
          }
        },
        {
          icon: 'send',
          name: i18n.t('invoices.invoices_issuing.quotation.header'),
          disabled: this.issuingType === 3 && this.$route.name === 'invoicesissuing',
          click: function () {
            presupuesto()
          }
        },
        {
          icon: 'send',
          name: i18n.t('invoices.invoices_issuing.simplified.header'),
          disabled: this.issuingType === 4 && this.$route.name === 'invoicesissuing',
          click: function () {
            simplificada()
          }
        }
      ] 
    },
    userOptions: function () {
      const showHelp = this.showHelp
      return [
        {
          icon: 'user',
          name: i18n.t('app.user_profile'),
          click: function ({ store }) {
            store.dispatch('auth/me', { navigate: true })
          }
        },
        {
          icon: 'help',
          name: i18n.t('app.help'),
          click: function () {
            showHelp()
          }
        },
        {
          icon: 'logout',
          name: i18n.t('app.logout'),
          click: function ({ store }) {
            store.dispatch('auth/logout')
          }
        }
      ] 
    },
    hasModuleVerifactu: function () { 
      return this.$store.getters['auth/hasVerifactuModule'] 
    },
    hasModuleReceivedInvoice: function () { 
      return this.$store.getters['auth/hasReceivedInvoiceModule'] 
    },
    hasModuleIssuedInvoice: function () { 
      return this.$store.getters['auth/hasIssuedInvoiceModule'] 
    },
    hasModuleInvoiceOrVerifactu: function () {
      return this.hasModuleIssuedInvoice || this.hasModuleReceivedInvoice || this.hasModuleVerifactu
    },
    hasModuleInterfazEmision: function () {
      return !!this.$store.getters['auth/hasInterfazEmisionModule'] && (this.hasModuleIssuedInvoice || this.hasModuleVerifactu)
    },
  },
  watch: {
    $route: 'routerChangeHandler',
    announcements: function (value, oldValue) {
      if (value?.length > oldValue?.length) {
        this.alreadyVisited = false
      }
    }
  },
  methods: {
    resetAlertsFilter () {
      this.$store.dispatch('app/updateidClienteFilterAlerts', null)
      this.$store.dispatch('app/getAnnouncementsList')
    },
    resetUploadsFilter () {
      this.$store.dispatch('app/updateidClienteFilterUpload', null)
    },
    resetIssuingFilter () {
      this.$store.dispatch('app/updateidClienteFilterIssuing', null)
    },
    onclickAnnouncement (action) {
      const data = { filters: [] }
      if (action === 'importacionesError') {
        data.filters.push({ type: 'tipoImportacionEstado', data: { value: 2, label: i18n.t('imports.status') } })
        this.pushRouteNameAux(data, 'configurations/imports')
      }
    },
    pushRouteNameAux (data, name) {
      this.$router.push(
        {
          name: name, 
          params: { data: data }, 
          query: { _r: this.guid() } 
        })
    },
    toggleUploadInvoice () {
      this.uploadInvoiceDialog = !this.uploadInvoiceDialog
    },
    
    /**
     * @param {Object} obj 
     * @param {Boolean} obj.background 
     * @param {{blnError: Boolean, codError: String, msgError: String, Respuesta: Boolean}[]|null} obj.results
     * @param {{idCliente: Number, idCIFEmpresa: Number, pgeType: String, autoSend: Boolean, facturas: Any[], recibidas: Boolean}} obj.params
     */
    applyUploadInvoice: async function ({ background = false, results = null, params = null }) {
      if (background) {
        this.uploadingInvoices = true
        this.toggleUploadInvoice()
      } else if (params?.facturas?.length > 0) {
        this.uploadInvoiceProgressData.idCliente = params.idCliente
        this.uploadInvoiceProgressData.idCIFEmpresa = params.idCIFEmpresa
        this.uploadInvoiceProgressData.pgeType = params.pgeType
        this.uploadInvoiceProgressData.autoSend = params.autoSend
        this.uploadInvoiceProgressData.facturas = params.facturas
        this.uploadInvoiceProgressData.recibidas = params.recibidas
        this.uploadingInvoices = true
        this.toggleUploadInvoice()
        this.toggleUploadProgressInvoice()
      } else {
        this.uploadingInvoices = false
        if (results?.length > 0) {
          const success = results.filter(x => !x.blnError)
          const errors = results.filter(x => x.blnError)
          if (errors.length > 0) {
            // TODO: Recargar avisos
            // this.$store.dispatch('app/getAnnouncementsList')

            // TODO: Generar avisos de errores
            const link = 'notices/uploaderrors'
            const aviso = this.$store.dispatch('app/getAnnouncement', { link })
            this.uuidList = aviso?.params?.map(x => x.uuid) ?? []
            errors.forEach(x => this.$set(x, 'uuid', this.generateErrorUuid()))
            this.$store.dispatch('app/updateAnnouncement', { link, NumeroAvisos: errors.length, Mensaje: i18n.t('feedback.error.invoice_upload_error'), params: errors })
          }
          if (success.length > 0) {
            bus.$emit('changeIt', 'changed menu')
          }
        }
      }
    },
    generateErrorUuid: function () {
      var uuid = ''
      do {
        uuid = crypto.randomUUID()
      } while (this.uuidList.includes(uuid))
      return uuid
    },
    toggleUploadProgressInvoice () {
      this.uploadInvoiceProgressData.isOpen = !this.uploadInvoiceProgressData.isOpen
    },
    applyUploadProgressInvoice: async function (data = { progress: [], success: true, background: false }) {
      if (data.background) {
        this.uploadingInvoices = true
        // TODO: Generar avisos de progreso
      } else {
        this.uploadingInvoices = false
        if (!data.success && data.progress.length > 0) {
          // TODO: Eliminar avisos de progreso
          // TODO: Generar avisos de errores
        }
      }
    },
    resetUploadProgressInvoice: function () {
      this.uploadInvoiceProgressData.idCliente = 0
      this.uploadInvoiceProgressData.idCIFEmpresa = 0
      this.uploadInvoiceProgressData.pgeType = null
      this.uploadInvoiceProgressData.autoSend = false
      this.uploadInvoiceProgressData.facturas = []
      this.uploadInvoiceProgressData.recibidas = false
    },
    showHelp: function () {
      this.toggleDialog()
    },
    toggleDialog: function () {
      this.helpDialogOpen = !this.helpDialogOpen
      if (!this.helpDialogOpen) {
        this.$store.dispatch('app/updatelastVisitedPatchNotes')
        this.$root.$emit('setControlFlagPatchNotes')
      }
    },
    routerChangeHandler: function () {
      //
    },
    toggleNotifications: function () {
      this.nots = !this.nots
      this.alreadyVisited = true
    },
    closeNotifications: function () {
      this.nots = false
    },
    toggleAvatar: function () {
      this.avatar = !this.avatar
    },
    closeAvatar: function () {
      this.avatar = false
    },
    toggleEmision: function () {
      this.emision = !this.emision
    },
    closeEmision: function () {
      this.emision = false
    },
    changeDarkMode: function () {
      this.$store.commit('app/setDarkMode', !this.darkMode)
    },
    gotoDashboard: function () {
      const uuid = this.guid()
      this.$router.push({ name: 'dashboard', params: { id: uuid } })
    },
    gotoIssuing: function () {
      setTransitionAllowed(1, this.isIssuingCreation)
      this.$router.push({ name: 'invoicesissuing', params: { issuingType: 1 } })
    },
    gotoProforma: function () {
      setTransitionAllowed(1, this.isIssuingCreation)
      this.$router.push({ name: 'invoicesissuing', params: { issuingType: 2 } })
    },
    gotoQuotation: function () {
      setTransitionAllowed(1, this.isIssuingCreation)
      this.$router.push({ name: 'invoicesissuing', params: { issuingType: 3 } })
    },
    gotoSimplified: function () {
      setTransitionAllowed(1, this.isIssuingCreation)
      this.$router.push({ name: 'invoicesissuing', params: { issuingType: 4 } })
    },
    guid: function () {
      return uuidv4()
    }
  },
}
</script>

<style lang="scss" scoped>
.w-aside-lg {
  width: 35rem;
}

.z-2010imp {
  z-index: 2010 !important;
}

.open-user-menu {
  &:active {
    transform: scale(0.96);
  }
}

.animate-hover {
  @apply transition-colors duration-300;
}

.badge {
  padding-left: 9px;
  padding-right: 9px;
  -webkit-border-radius: 9px;
  -moz-border-radius: 9px;
  border-radius: 9px;
}

.label-warning[href],
.badge-warning[href] {
  background-color: #c67605;
}

#lblCartCount {
  font-size: 12px;
  background: #ff0000;
  color: #fff;
  padding: 0 5px;
  vertical-align: top;
  margin-left: -10px;
  margin-top: -15px;
}

.max-width-logo {
  width: 500px;
  max-width: 500px;
}

.cart-count {
  font-size: 16px;
  @apply bg-primary-300;
  @apply dark:bg-primary-500;
  color: #fff;
  padding: 5px 10px;
  vertical-align: center;
  margin-top: -15px;
}

.badge-count {
  font-size: 12px;
  background: #ff0000;
  color: #fff;
  padding: 0 5px;
  vertical-align: top;
  margin-left: -10px;
  margin-top: -15px;
}

.badge-count-visited {
  font-size: 12px;
  @apply bg-primary-300;
  @apply dark:bg-primary-500;
  color: #fff;
  padding: 0 5px;
  vertical-align: top;
  margin-left: -10px;
  margin-top: -15px;
}
</style>
